import { useEffect, useMemo, useState } from 'preact/hooks';
import type { JSX } from 'preact';
import { rsvp as copy } from '../content/wedding';
import {
  ADMIN_CSV_URL,
  adminLogin,
  adminLogout,
  adminParties,
  ApiError,
  type AdminList,
  type AdminParty,
} from '../lib/api';

/**
 * The guest list.
 *
 * Unlisted rather than secret: the path is configurable so it need not live in
 * a public repo, but nothing here is load bearing. Every byte on this page
 * comes from /api/admin/*, which the Worker refuses without a signed session
 * cookie, so a stranger who finds the URL gets an empty shell and a 401.
 *
 * It is also read-only on purpose. Invitations are created by a script and a
 * `wrangler d1 execute`, which means there is no write path to attack.
 */

type Filter = 'all' | 'awaiting' | 'partial' | 'replied' | 'attending' | 'declined';

/**
 * The plates, in menu order, for the tally strip and the guest lines.
 *
 * Built from the same copy the form offers, so the words on this page are the
 * words the guest read. `always` marks the two that are drawn even at nought,
 * because "no beef at all" is itself the answer to a question the caterer asks.
 */
const MEAL_TILES = [
  ...copy.meals.map((meal) => ({ id: meal.id, label: meal.label, always: true })),
  ...copy.diets.map((diet) => ({ id: diet.id, label: diet.label, always: false })),
];

const MEAL_LABELS = new Map<string, string>(
  MEAL_TILES.map((tile) => [tile.id, tile.label]),
);

/** What a guest's line says they are eating. Empty for a reply written before
    the question existed, which is the one case with nothing to show. */
function mealLabel(meal: string): string {
  return MEAL_LABELS.get(meal) ?? meal;
}

const FILTERS: { id: Filter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'awaiting', label: 'Awaiting' },
  { id: 'partial', label: 'Part answered' },
  { id: 'replied', label: 'Replied' },
  { id: 'attending', label: 'Coming' },
  { id: 'declined', label: 'Not coming' },
];

/** Named guests on this invitation who have still not said either way. */
function outstanding(party: AdminParty): string[] {
  return party.guests.filter((g) => !g.isPlusOne && g.attending === null).map((g) => g.name);
}

function matches(party: AdminParty, filter: Filter): boolean {
  switch (filter) {
    case 'awaiting':
      return party.respondedAt === null;
    // The state a partial reply creates, and the one worth chasing: they have
    // written back, and somebody on the envelope is still unaccounted for.
    case 'partial':
      return party.respondedAt !== null && outstanding(party).length > 0;
    case 'replied':
      return party.respondedAt !== null;
    case 'attending':
      return party.guests.some((guest) => guest.attending === true);
    case 'declined':
      return party.respondedAt !== null && party.guests.every((guest) => guest.attending !== true);
    default:
      return true;
  }
}

function haystack(party: AdminParty): string {
  return [
    party.displayName,
    party.lookupZip,
    party.contactEmail,
    party.adminNote,
    ...party.guests.map((g) => `${g.name} ${g.meal} ${g.dietary}`),
  ]
    .join(' ')
    .toLowerCase();
}

export function Admin() {
  const [phase, setPhase] = useState<'checking' | 'locked' | 'open'>('checking');
  const [data, setData] = useState<AdminList | null>(null);
  const [key, setKey] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const [filter, setFilter] = useState<Filter>('all');
  const [query, setQuery] = useState('');

  async function load() {
    const list = await adminParties();
    setData(list);
    setPhase('open');
  }

  // An unexpired cookie from earlier means no second sign-in.
  useEffect(() => {
    adminParties()
      .then((list) => {
        setData(list);
        setPhase('open');
      })
      .catch(() => setPhase('locked'));
  }, []);

  async function onLogin(event: JSX.TargetedEvent<HTMLFormElement, Event>) {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      await adminLogin(key.trim());
      setKey('');
      await load();
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : 'Could not sign in.');
    } finally {
      setBusy(false);
    }
  }

  async function onLogout() {
    await adminLogout().catch(() => undefined);
    setData(null);
    setPhase('locked');
  }

  const visible = useMemo(() => {
    if (!data) return [];
    const needle = query.trim().toLowerCase();
    return data.parties.filter(
      (party) => matches(party, filter) && (needle === '' || haystack(party).includes(needle)),
    );
  }, [data, filter, query]);

  if (phase === 'checking') {
    return (
      <main class="admin admin--gate">
        <p class="admin__wait">One moment…</p>
      </main>
    );
  }

  if (phase === 'locked') {
    return (
      <main class="admin admin--gate">
        <form class="admin__login" onSubmit={onLogin}>
          <h1>Guest list</h1>
          <label class="field__label" for="key">
            Admin key
          </label>
          <input
            id="key"
            type="password"
            autocomplete="off"
            spellcheck={false}
            value={key}
            placeholder="Paste the key"
            onInput={(e) => setKey(e.currentTarget.value)}
          />
          {error && (
            <p class="admin__error" role="alert">
              {error}
            </p>
          )}
          <button class="btn" type="submit" disabled={busy || key.trim() === ''}>
            {busy ? 'Checking…' : 'Sign in'}
          </button>
        </form>
      </main>
    );
  }

  const summary = data?.summary;

  return (
    <main class="admin">
      <header class="admin__bar">
        <h1>Guest list</h1>
        <div class="admin__barActions">
          <a class="admin__link" href={ADMIN_CSV_URL}>
            Download CSV
          </a>
          <button class="admin__link" type="button" onClick={onLogout}>
            Sign out
          </button>
        </div>
      </header>

      {summary && (
        <dl class="admin__tally">
          <div>
            <dt>Invitations</dt>
            <dd>{summary.parties}</dd>
          </div>
          <div>
            <dt>Replied</dt>
            <dd>{summary.replied}</dd>
          </div>
          <div>
            <dt>Awaiting</dt>
            <dd>{summary.awaiting}</dd>
          </div>
          <div>
            <dt>Part answered</dt>
            <dd>{summary.partial}</dd>
          </div>
          <div>
            <dt>Coming</dt>
            <dd>{summary.attending}</dd>
          </div>
          <div>
            <dt>Not coming</dt>
            <dd>{summary.declined}</dd>
          </div>
          {/* The kitchen's order, one tile per plate. The two plates are
              always drawn, nought and all — a nought against beef is a number
              the caterer asked for. The seven behind "Other" are drawn only
              once somebody has asked for one, or the strip becomes nine mostly
              empty boxes. */}
          {MEAL_TILES.filter(
            (tile) => tile.always || summary.meals[tile.id] > 0,
          ).map((tile) => (
            <div key={tile.id}>
              <dt>{tile.label}</dt>
              <dd>{summary.meals[tile.id]}</dd>
            </div>
          ))}
          {summary.meals[''] > 0 && (
            <div>
              <dt>No dinner</dt>
              <dd>
                {summary.meals['']} <span class="admin__of">to chase</span>
              </dd>
            </div>
          )}
          <div>
            <dt>Plus ones</dt>
            <dd>
              {summary.plusOnesClaimed} <span class="admin__of">of {summary.plusOnesOffered}</span>
            </dd>
          </div>
        </dl>
      )}

      <div class="admin__controls">
        <div class="admin__filters" role="tablist" aria-label="Filter">
          {FILTERS.map((option) => (
            <button
              key={option.id}
              type="button"
              role="tab"
              aria-selected={filter === option.id}
              class={`admin__filter ${filter === option.id ? 'is-on' : ''}`}
              onClick={() => setFilter(option.id)}
            >
              {option.label}
            </button>
          ))}
        </div>
        <input
          class="admin__search"
          type="search"
          value={query}
          placeholder="Search names or notes"
          aria-label="Search the guest list"
          onInput={(e) => setQuery(e.currentTarget.value)}
        />
      </div>

      <p class="admin__count">
        {visible.length} of {data?.parties.length ?? 0} invitations
      </p>

      <ul class="admin__list">
        {visible.map((party) => (
          <li key={party.id} class={`card ${party.respondedAt ? '' : 'card--awaiting'}`}>
            <div class="card__head">
              <h2>{party.displayName}</h2>
              <span class="card__when">
                {party.respondedAt
                  ? `Replied ${new Date(party.respondedAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}${party.replyCount > 1 ? ` · amended ${party.replyCount - 1}×` : ''}${outstanding(party).length > 0 ? ` · ${outstanding(party).length} still to answer` : ''}`
                  : 'No reply yet'}
              </span>
            </div>

            <ul class="card__guests">
              {party.guests.flatMap((guest) => {
                // A plus one that has been offered and not taken up is not a
                // row in the database — it is a flag on the person who was
                // offered it, so it is drawn here rather than counted.
                const unclaimed =
                  guest.plusOneAllowed &&
                  !party.guests.some((other) => other.hostId === guest.id);
                const host = guest.hostId
                  ? party.guests.find((other) => other.id === guest.hostId)
                  : undefined;
                return [
                    <li key={guest.id}>
                      <span class="card__guestName">
                        {guest.name}
                        {host && <em> · {host.name.split(' ')[0]}’s guest</em>}
                      </span>
                      <span
                        class={`pill ${guest.attending === true ? 'pill--yes' : guest.attending === false ? 'pill--no' : ''}`}
                      >
                        {guest.attending === true ? 'Coming' : guest.attending === false ? 'Not coming' : 'Awaiting'}
                      </span>
                      {/* Only for someone who is coming: nobody else has a
                          plate, and the database blanks it when an answer
                          changes. "No plate" is drawn rather than left out,
                          because it is the row that needs a decision — that
                          guest told us what they can eat instead of picking,
                          and somebody has to order for them. */}
                      {guest.attending === true && (
                        <span class={`card__meal ${guest.meal === '' ? 'card__meal--none' : ''}`}>
                          {guest.meal === '' ? 'No dinner chosen' : mealLabel(guest.meal)}
                        </span>
                      )}
                      {guest.dietary && <span class="card__diet">{guest.dietary}</span>}
                    </li>,
                    unclaimed ? (
                      <li key={`${guest.id}-plus`} class="card__unclaimed">
                        <span class="card__guestName">
                          <em>{guest.name.split(' ')[0]}’s plus one</em>
                        </span>
                        <span class="pill">Unclaimed</span>
                      </li>
                    ) : null,
                ];
              })}
            </ul>

            <dl class="card__meta">
              {/* The postcode is half of how this household gets in — the
                  other half is any name on the invitation above. Worth having
                  on the card for the phone call that starts "it says it cannot
                  find us": it is the one thing we hold and they have to
                  match. */}
              <dt>Postcode</dt>
              <dd>{party.lookupZip || <em>none — any Lubbock code</em>}</dd>
              {party.contactEmail && (
                <>
                  <dt>Email</dt>
                  <dd>
                    <a href={`mailto:${party.contactEmail}`}>{party.contactEmail}</a>
                  </dd>
                </>
              )}
              {party.message && (
                <>
                  <dt>Note</dt>
                  <dd>{party.message}</dd>
                </>
              )}
              {party.adminNote && (
                <>
                  <dt>Ours</dt>
                  <dd class="card__private">{party.adminNote}</dd>
                </>
              )}
            </dl>
          </li>
        ))}
      </ul>

      {visible.length === 0 && <p class="admin__empty">Nothing matches that.</p>}
    </main>
  );
}
